// /src/app/modules/cuentas/ListadoCuentasPage.tsx
import React, {useState, useEffect, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {fetchWithAuth} from '../../core/utils/fetchWithAuth'
import '../socios/socios.scss'

// Tipos actualizados para coincidir con la respuesta del backend
type Movimiento = {
  id: string
  nroFactura: string
  idPago: string
  fecha: string
  monto: string
  tipo: string
  estado: string
}

type Resumen = {
  totalEgresos: number
  totalIngresos: number
  balance: number
}

type Cuenta = {
  numero: number
  activa: number
  idMiembro: number
  nombreFantasia: string
  razonSocial: string
  socio: number
  categoriaId: number
  categoriaNombre: string
  zonaCobro: number | null
  exentaIva: number
  exentaIrae: number
  movimientos: Movimiento[]
  resumen: Resumen
}

function ListadoCuentasPage() {
  const navigate = useNavigate()
  
  // Estados para los datos y la carga
  const [cuentas, setCuentas] = useState<Cuenta[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  
  // Estados para la paginación del backend
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)
  
  // Estados para el typeahead
  const [searchQuery, setSearchQuery] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions, setSuggestions] = useState<Cuenta[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  
  // Índice de la fila que está expandida (o null si ninguna)
  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  // Función para obtener el token del localStorage
  const getToken = (): string | null => {
    return localStorage.getItem('X-TOKEN')
  }

  // Función para manejar respuestas de autenticación
  const handleAuthResponse = (response: Response, data: any): boolean => {
    if (response.status === 401 || 
        (data.code === 'ERROR' && data.msg === 'No Autorizado') ||
        (data.code === 'ERROR' && data.returned_message?.includes('JWT expired'))) {
      localStorage.removeItem('X-TOKEN')
      navigate('/auth/login')
      return true
    }
    return false
  }

  // Función para obtener las cuentas del backend
  const fetchCuentas = async (page: number = 1, query: string = '') => {
    try {
      setLoading(true)
      setError(null)
      
      const token = getToken()
      if (!token) {
        throw new Error('No se encontró el token de autenticación')
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token
        },
        body: JSON.stringify({
          service: 'getCuentas',
          current_page: page,
          q: query
        })
      })

      if (!response.ok) {
        throw new Error(`Error en la petición: ${response.status}`)
      }

      const data = await response.json()
      
      if (handleAuthResponse(response, data)) {
        return
      }
      
      if (data.code === 'OK') {
        setCuentas(data.data.cuentas)
        setCurrentPage(data.data.page)
        setTotalPages(data.data.totalPages)
        setTotal(data.data.total)
      } else {
        throw new Error(data.returned_message || 'Error en la respuesta del servidor')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Función para búsqueda typeahead
  const searchCuentas = async (query: string) => {
    if (!query.trim()) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    try {
      setSearchLoading(true)
      const token = getToken()
      if (!token) return

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token
        },
        body: JSON.stringify({
          service: 'getCuentas',
          current_page: 1,
          q: query
        })
      })

      if (!response.ok) return

      const data = await response.json()
      
      if (handleAuthResponse(response, data)) {
        return
      }
      
      if (data.code === 'OK') {
        setSuggestions(data.data.cuentas)
        setShowSuggestions(true)
      }
    } catch (err) {
      console.error('Error en búsqueda:', err)
    } finally {
      setSearchLoading(false)
    }
  }

  // Debounce para el typeahead
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchCuentas(searchQuery)
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery])

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Cargar los datos al montar el componente
  useEffect(() => {
    fetchCuentas(currentPage)
  }, [currentPage])

  // Función para cambiar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Manejar selección de sugerencia
  const handleSelectSuggestion = (cuenta: Cuenta) => {
    setSearchQuery('')
    setShowSuggestions(false)
    navigate(`/cuentas/${cuenta.numero}`)
  }

  // Manejar búsqueda manual
  const handleSearch = () => {
    setShowSuggestions(false)
    fetchCuentas(1, searchQuery)
  }

  // Limpiar búsqueda
  const handleClearSearch = () => {
    setSearchQuery('')
    setShowSuggestions(false)
    fetchCuentas(1, '')
  }

  // Expansión de fila
  const toggleExpandRow = (index: number) => {
    if (expandedRow === index) {
      setExpandedRow(null)
    } else {
      setExpandedRow(index)
    }
  }

  // Navegar al detalle de cuenta
  const handleVerDetalle = (numero: number) => {
    navigate(`/cuentas/${numero}`)
  }

  return (
    <div className='card'>
      {/* Header con búsqueda typeahead mejorada */}
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <div className='d-flex align-items-center'>
            <i className='ki-duotone ki-magnifier fs-2 me-2 text-primary'>
              <span className='path1'></span>
              <span className='path2'></span>
            </i>
            <h3 className='fw-bold m-0'>Gestión de Cuentas</h3>
          </div>
        </div>
        <div className='card-toolbar'>
          {/* Typeahead Search */}
          <div className='position-relative' style={{minWidth: '400px'}} ref={searchRef}>
            <div className='input-group'>
              <span className='input-group-text'>
                <i className='ki-duotone ki-magnifier fs-2'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                </i>
              </span>
              <input
                type='text'
                className='form-control form-control-lg'
                placeholder='Buscar por cuenta, socio, nombre o razón social...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              {searchQuery && (
                <button 
                  className='btn btn-icon btn-light-danger' 
                  onClick={handleClearSearch}
                  type='button'
                >
                  <i className='ki-duotone ki-cross fs-2'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                  </i>
                </button>
              )}
              <button 
                className='btn btn-primary' 
                onClick={handleSearch}
                type='button'
              >
                Buscar
              </button>
            </div>

            {/* Sugerencias del typeahead */}
            {showSuggestions && (
              <div 
                className='position-absolute w-100 bg-white shadow-lg rounded mt-1' 
                style={{zIndex: 1050, maxHeight: '400px', overflowY: 'auto'}}
              >
                {searchLoading ? (
                  <div className='p-4 text-center'>
                    <div className='spinner-border spinner-border-sm text-primary' role='status'>
                      <span className='visually-hidden'>Cargando...</span>
                    </div>
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className='list-group list-group-flush'>
                    {suggestions.map((cuenta) => (
                      <button
                        key={cuenta.numero}
                        className='list-group-item list-group-item-action d-flex justify-content-between align-items-center'
                        onClick={() => handleSelectSuggestion(cuenta)}
                        type='button'
                      >
                        <div>
                          <div className='d-flex align-items-center'>
                            <span className='badge badge-light-primary me-2'>#{cuenta.numero}</span>
                            <span className='badge badge-light-info me-2'>Socio: {cuenta.socio}</span>
                            <strong className='me-2'>{cuenta.nombreFantasia}</strong>
                          </div>
                          <small className='text-muted d-block mt-1'>{cuenta.razonSocial}</small>
                        </div>
                        <i className='ki-duotone ki-arrow-right fs-3 text-muted'>
                          <span className='path1'></span>
                          <span className='path2'></span>
                        </i>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className='p-4 text-center text-muted'>
                    <i className='ki-duotone ki-information-5 fs-2x mb-2'>
                      <span className='path1'></span>
                      <span className='path2'></span>
                      <span className='path3'></span>
                    </i>
                    <p className='m-0'>No se encontraron resultados</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='card-body pt-0'>
        {/* Información de paginación */}
        {!loading && total > 0 && (
          <div className='d-flex align-items-center mb-4 bg-light-primary p-3 rounded'>
            <i className='ki-duotone ki-information-5 fs-2 text-primary me-2'>
              <span className='path1'></span>
              <span className='path2'></span>
              <span className='path3'></span>
            </i>
            <span className='fw-semibold text-gray-800'>
              Mostrando {cuentas.length} de {total} cuentas totales
            </span>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className='py-3'>
            <div className='table-responsive'>
              <table className='table'>
                <thead>
                  <tr className='sticky-head'><th colSpan={7}>&nbsp;</th></tr>
                </thead>
                <tbody>
                  {Array.from({length: 6}).map((_, i) => (
                    <tr key={`sk-${i}`}>
                      {Array.from({length: 7}).map((__, j) => (
                        <td key={`skc-${i}-${j}`}>
                          <div className='placeholder-glow'><span className='placeholder col-8'></span></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className='alert alert-danger d-flex align-items-center p-5'>
            <i className='ki-duotone ki-information-5 fs-2hx text-danger me-4'>
              <span className='path1'></span>
              <span className='path2'></span>
              <span className='path3'></span>
            </i>
            <div className='d-flex flex-column'>
              <h4 className='mb-1 text-danger'>Error</h4>
              <span>{error}</span>
            </div>
            <button 
              className='btn btn-sm btn-light-primary ms-auto'
              onClick={() => fetchCuentas(currentPage)}
            >
              <i className='ki-duotone ki-arrows-circle fs-3'>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
              Reintentar
            </button>
          </div>
        )}

        {/* Tabla de cuentas */}
        {!loading && !error && (
          <>
            <div className='table-responsive'>
              <table className='table table-row-dashed table-striped table-hover align-middle gs-0'>
                <thead>
                  <tr className='fw-bold text-muted bg-light p-3 sticky-head'>
                    <th style={{width: '80px'}}>Nro</th>
                    <th style={{width: '80px'}}>Socio</th>
                    <th style={{width: '80px'}}>Estado</th>
                    <th style={{minWidth: '150px'}}>Nombre Fantasía</th>
                    <th style={{minWidth: '150px'}}>Razón Social</th>
                    <th style={{minWidth: '100px'}}>Categoría</th>
                    <th style={{width: '100px', textAlign: 'center'}}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cuentas.map((cuenta, index) => {
                    const isExpanded = expandedRow === index
                    const ultimos6Movimientos = cuenta.movimientos.slice(-6).reverse()

                    return (
                      <React.Fragment key={cuenta.numero}>
                        {/* Fila principal */}
                        <tr>
                          <td>
                            <button
                              className='btn btn-sm btn-icon btn-light-primary'
                              onClick={() => toggleExpandRow(index)}
                              title={isExpanded ? 'Cerrar' : 'Ver movimientos'}
                            >
                              <i className={`ki-duotone ki-${isExpanded ? 'up' : 'down'} fs-3`}>
                                <span className='path1'></span>
                                <span className='path2'></span>
                              </i>
                            </button>
                            <span className='ms-2 fw-bold'>{cuenta.numero}</span>
                          </td>
                          <td>
                            <span className='badge badge-light-info'>{cuenta.socio}</span>
                          </td>
                          <td>
                            {cuenta.activa === 1 ? (
                              <span className='badge badge-light-success'>Activa</span>
                            ) : (
                              <span className='badge badge-light-danger'>Inactiva</span>
                            )}
                          </td>
                          <td>
                            <div className='d-flex flex-column'>
                              <span className='fw-bold text-gray-800'>{cuenta.nombreFantasia}</span>
                              <span className='text-muted fs-7'>Zona: {cuenta.zonaCobro || 'N/A'}</span>
                            </div>
                          </td>
                          <td>{cuenta.razonSocial}</td>
                          <td>
                            <span className='badge badge-light-primary'>{cuenta.categoriaNombre}</span>
                          </td>
                          <td className='text-center'>
                            <button
                              className='btn btn-sm btn-primary'
                              onClick={() => handleVerDetalle(cuenta.numero)}
                            >
                              <i className='ki-duotone ki-eye fs-4 me-1'>
                                <span className='path1'></span>
                                <span className='path2'></span>
                                <span className='path3'></span>
                              </i>
                              Ver
                            </button>
                          </td>
                        </tr>

                        {/* Fila expandida con últimos 6 movimientos */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} style={{padding: 0}}>
                              <div className='p-6 bg-light rounded' style={{margin: '0.5rem'}}>
                                {/* Header con resumen */}
                                <div className='d-flex justify-content-between align-items-center mb-5 pb-4 border-bottom border-gray-300'>
                                  <div className='d-flex align-items-center'>
                                    <div className='symbol symbol-45px me-3 bg-light-info'>
                                      <i className='ki-duotone ki-chart-simple fs-2x text-info'>
                                        <span className='path1'></span>
                                        <span className='path2'></span>
                                        <span className='path3'></span>
                                        <span className='path4'></span>
                                      </i>
                                    </div>
                                    <div>
                                      <h5 className='fw-bold text-gray-800 mb-1'>
                                        Últimos 6 Movimientos
                                      </h5>
                                      <span className='text-muted fs-7'>Cuenta #{cuenta.numero}</span>
                                    </div>
                                  </div>
                                  <div className='d-flex gap-3'>
                                    <div className='d-flex flex-column align-items-end p-3 bg-white rounded shadow-sm'>
                                      <span className='text-muted fs-7 mb-1'>Ingresos</span>
                                      <span className='fw-bold fs-5 text-success'>
                                        ${cuenta.resumen.totalIngresos.toFixed(2)}
                                      </span>
                                    </div>
                                    <div className='d-flex flex-column align-items-end p-3 bg-white rounded shadow-sm'>
                                      <span className='text-muted fs-7 mb-1'>Egresos</span>
                                      <span className='fw-bold fs-5 text-danger'>
                                        ${cuenta.resumen.totalEgresos.toFixed(2)}
                                      </span>
                                    </div>
                                    <div className='d-flex flex-column align-items-end p-3 bg-white rounded shadow-sm'>
                                      <span className='text-muted fs-7 mb-1'>Balance</span>
                                      <span className={`fw-bold fs-5 ${cuenta.resumen.balance >= 0 ? 'text-success' : 'text-danger'}`}>
                                        ${cuenta.resumen.balance.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Tabla de movimientos */}
                                {ultimos6Movimientos.length > 0 ? (
                                  <div className='card shadow-sm'>
                                    <div className='card-body p-0'>
                                      <div className='table-responsive'>
                                        <table className='table table-hover align-middle gs-0 mb-0'>
                                          <thead>
                                            <tr className='fw-bold text-gray-700 bg-light-secondary border-bottom-2 border-gray-300'>
                                              <th className='ps-4 rounded-start' style={{width: '120px'}}>Nro Factura</th>
                                              <th style={{width: '120px'}}>Fecha</th>
                                              <th className='text-end' style={{width: '140px'}}>Monto</th>
                                              <th className='text-center' style={{width: '120px'}}>Tipo</th>
                                              <th className='pe-4 text-center rounded-end' style={{width: '120px'}}>Estado</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {ultimos6Movimientos.map((mov, idx) => (
                                              <tr key={mov.id} className='border-bottom border-gray-200'>
                                                <td className='ps-4'>
                                                  <div className='d-flex align-items-center'>
                                                    <i className='ki-duotone ki-document fs-2 text-gray-400 me-2'>
                                                      <span className='path1'></span>
                                                      <span className='path2'></span>
                                                    </i>
                                                    <span className='fw-semibold text-gray-800'>{mov.nroFactura}</span>
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className='text-gray-700'>{mov.fecha}</span>
                                                </td>
                                                <td className='text-end'>
                                                  <span className={`fw-bold fs-6 ${mov.tipo === 'DEBE' ? 'text-danger' : 'text-success'}`}>
                                                    ${parseFloat(mov.monto).toFixed(2)}
                                                  </span>
                                                </td>
                                                <td className='text-center'>
                                                  <span className={`badge ${mov.tipo === 'DEBE' ? 'badge-light-danger' : 'badge-light-success'} fw-semibold`}>
                                                    {mov.tipo === 'DEBE' ? (
                                                      <><i className='ki-duotone ki-arrow-down fs-4 me-1'><span className='path1'></span><span className='path2'></span></i> DEBE</>
                                                    ) : (
                                                      <><i className='ki-duotone ki-arrow-up fs-4 me-1'><span className='path1'></span><span className='path2'></span></i> HABER</>
                                                    )}
                                                  </span>
                                                </td>
                                                <td className='pe-4 text-center'>
                                                  <span className='badge badge-light-info'>{mov.estado || 'Pendiente'}</span>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className='card shadow-sm'>
                                    <div className='card-body'>
                                      <div className='d-flex flex-column align-items-center py-8'>
                                        <i className='ki-duotone ki-file fs-3x text-gray-400 mb-4'>
                                          <span className='path1'></span>
                                          <span className='path2'></span>
                                        </i>
                                        <h5 className='text-gray-700 mb-2'>No hay movimientos registrados</h5>
                                        <p className='text-muted mb-0 fs-7'>Esta cuenta no tiene movimientos recientes.</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}

                  {/* Estado vacío */}
                  {cuentas.length === 0 && (
                    <tr>
                      <td colSpan={7} className='text-center py-10'>
                        <div className='d-flex flex-column align-items-center gap-3'>
                          <i className='ki-duotone ki-file-deleted fs-3x text-muted'>
                            <span className='path1'></span>
                            <span className='path2'></span>
                          </i>
                          <div>
                            <h5 className='text-muted'>No se encontraron cuentas</h5>
                            <p className='text-muted mb-0'>Intenta ajustar los filtros de búsqueda</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación mejorada */}
            {totalPages > 1 && (
              <div className='d-flex flex-stack flex-wrap pt-6'>
                <div className='fs-6 fw-semibold text-gray-700'>
                  Página {currentPage} de {totalPages} - Total: {total} cuentas
                </div>
                <ul className='pagination'>
                  <li className={`page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className='previous'></i>
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, idx) => {
                    const pageNum = idx + 1
                    // Mostrar solo páginas cercanas a la actual
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                    ) {
                      return (
                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      )
                    } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                      return <li key={pageNum} className='page-item disabled'><span className='page-link'>...</span></li>
                    }
                    return null
                  })}
                  <li className={`page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <i className='next'></i>
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ListadoCuentasPage
